import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { Button, Icon, Loading, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  useLazyGetClientPaymentMethodQuery,
  useLazyGetClientPaymentProviderKeysQuery,
  useUpdateCardNicknameMutation,
} from "@/redux/apis/clientPayment/clientPaymentApi";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";
import { addToast } from "@/utils";
import { ResponsiveDialog } from "./ResponsiveDialog";

const CARD_FORM_ID = "client-card-form";
/** The backend persists the card via Stripe webhook — wait before confirming (same as PaymentMethodSetupPanel) */
const WEBHOOK_WAIT_MS = 8000;
const WEBHOOK_RETRY_MS = 4000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface CardFormModalProps {
  open: boolean;
  mode: "add" | "edit";
  accountId: string;
  /** card being edited (edit mode only) — saving replaces it */
  card?: ClientPaymentMethod;
  onClose: () => void;
  /** called once the backend has confirmed the new card; edit mode deletes the old card here */
  onSaved: (newPaymentMethodId: string, replacedCard?: ClientPaymentMethod, isRename?: boolean) => Promise<void>;
  /** edit mode: open the delete confirmation */
  onRequestDelete?: (card: ClientPaymentMethod) => void;
}

interface CardChangeState {
  complete: boolean;
  empty: boolean;
}

interface CardFormFieldsProps {
  onReadyChange: (ready: boolean) => void;
  onSubmittingChange: (submitting: boolean) => void;
  onConfirmed: (setupIntentId: string) => Promise<void>;
  onCardChange: (state: CardChangeState) => void;
}

const CardFormFields = ({ onReadyChange, onSubmittingChange, onConfirmed, onCardChange }: CardFormFieldsProps) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    onReadyChange(!!stripe && !!elements);
  }, [stripe, elements, onReadyChange]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    onSubmittingChange(true);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (result.error) {
      addToast("danger", result.error.message || "Something went wrong. Please try again.");
      onSubmittingChange(false);
      return;
    }

    try {
      await onConfirmed(result.setupIntent.id);
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <form id={CARD_FORM_ID} onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          paymentMethodOrder: ["card"],
          layout: { type: "tabs", defaultCollapsed: false },
          // the branded legal copy below the form replaces Stripe's own terms text
          terms: { card: "never" },
        }}
        onChange={(e) => onCardChange({ complete: e.complete, empty: e.empty })}
      />
    </form>
  );
};

export const CardFormModal = ({ open, mode, accountId, card, onClose, onSaved, onRequestDelete }: CardFormModalProps) => {
  const operatorName = useAppSelector(configSelector)?.name;
  const [nickname, setNickname] = useState(card?.card_data?.nickname ?? "");
  const [cardEmpty, setCardEmpty] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getKeys, { data: keys, isFetching: isFetchingKeys, error: keysError }] = useLazyGetClientPaymentProviderKeysQuery();
  const [getPaymentMethod] = useLazyGetClientPaymentMethodQuery();
  const [updateNickname] = useUpdateCardNicknameMutation();

  useEffect(() => {
    if (!open) return;
    setNickname(card?.card_data?.nickname ?? "");
    setCardEmpty(true);
    setCardComplete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // SetupIntents are single-use — always fetch fresh keys (no label: backend auto-assigns type)
    if (open && accountId) getKeys({ accountId });
  }, [open, accountId, getKeys]);

  const stripePromise = useMemo(() => (keys?.publishable_key ? loadStripe(keys.publishable_key) : null), [keys?.publishable_key]);
  const clientSecret = keys?.intent_secret;

  const handleConfirmed = async (setupIntentId: string) => {
    // The card is persisted by a Stripe webhook after confirmSetup, so poll for it: wait, then
    // retry once more before giving up (matches the mobile app's 8s + 4s verification).
    const fetchSaved = () => getPaymentMethod({ accountId, paymentMethodId: setupIntentId }).unwrap();
    try {
      await wait(WEBHOOK_WAIT_MS);
      let saved;
      try {
        saved = await fetchSaved();
      } catch {
        await wait(WEBHOOK_RETRY_MS);
        saved = await fetchSaved();
      }
      // Only use the real payment-method uuid returned by the backend — never PATCH against a SetupIntent id.
      const realUuid = saved?.uuid;
      const bestId = realUuid ?? setupIntentId;
      const trimmedNickname = nickname.trim();
      if (trimmedNickname) {
        if (realUuid) {
          try {
            await updateNickname({ accountId, paymentMethodId: realUuid, nickname: trimmedNickname }).unwrap();
          } catch {
            addToast("info", "Card added — nickname couldn't be saved, you can rename it from the list.");
          }
        } else {
          // Poll never returned a real uuid — skip the PATCH to avoid patching a SetupIntent id.
          addToast("info", "Card added — nickname couldn't be saved, you can rename it from the list.");
        }
      }
      await onSaved(bestId, mode === "edit" ? card : undefined);
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((message) => addToast("danger", message));
    }
  };

  const handleRenameOnly = async () => {
    if (!card) return;
    setIsSubmitting(true);
    try {
      await updateNickname({ accountId, paymentMethodId: card.uuid, nickname: nickname.trim() }).unwrap();
      await onSaved(card.uuid, undefined, true);
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((message) => addToast("danger", message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit with the card field completely empty → rename only (uuid preserved). Otherwise re-tokenize.
  // A partial card entry (!cardEmpty && !cardComplete) is NOT rename-only — the form blocks Save until complete.
  const isRenameOnly = mode === "edit" && cardEmpty;

  const renderButtons = (isMobile: boolean) => {
    const saveButton = (
      <Button
        variant="primary"
        size="lg"
        // submit via requestSubmit instead of the form attribute — headlessui's Dialog
        // swallows the implicit form-association submit when Stripe iframes hold focus
        onClick={() => (isRenameOnly ? handleRenameOnly() : document.forms.namedItem(CARD_FORM_ID)?.requestSubmit())}
        disabled={isSubmitting || (!isRenameOnly && (!isFormReady || !cardComplete))}
        className={isMobile ? "w-full" : undefined}
      >
        Save
      </Button>
    );
    const cancelButton = isMobile ? (
      <button type="button" className="h-10 w-full disabled:opacity-50" onClick={onClose} disabled={isSubmitting}>
        <Typography variant="action" className="text-info">
          Cancel
        </Typography>
      </button>
    ) : (
      <Button variant="secondary" size="lg" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
    );

    return isMobile ? (
      <>
        {saveButton}
        {cancelButton}
      </>
    ) : (
      <>
        {cancelButton}
        {saveButton}
      </>
    );
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={isSubmitting ? () => undefined : onClose}
      dismissible={false}
      title={mode === "add" ? "Add New Credit Card" : "Edit Credit Card"}
      description="Fill up the credit card information"
      renderButtons={renderButtons}
    >
      <div className="relative flex min-h-[120px] flex-col gap-4">
        {(isFetchingKeys || isSubmitting) && <Loading />}

        {mode === "edit" && card?.metadata.in_use && (
          <div className="flex items-center gap-2 rounded-lg border border-warning bg-warning-light py-1.5 pl-2 pr-4">
            <Icon name="Danger" variant="Bold" size={20} className="shrink-0 text-warning" />
            <div className="flex flex-col">
              <Typography variant="action" className="text-warning-dark">
                There is an upcoming trip using this payment method
              </Typography>
              <Typography variant="small-regular" className="!font-medium text-warning-dark">
                Updating this card automatically applies the changes to any upcoming trips. It can&apos;t be removed while in use.
              </Typography>
            </div>
          </div>
        )}

        {keysError ? (
          <Typography variant="paragraph" className="text-danger">
            {getErrorMessages(keysError)[0] ?? "Unable to start the card setup. Please try again."}
          </Typography>
        ) : (
          stripePromise &&
          clientSecret && (
            <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
              <CardFormFields
                onReadyChange={setIsFormReady}
                onSubmittingChange={setIsSubmitting}
                onConfirmed={handleConfirmed}
                onCardChange={({ complete, empty }) => {
                  setCardComplete(complete);
                  setCardEmpty(empty);
                }}
              />
            </Elements>
          )
        )}

        <Typography variant="paragraph" className="text-neutral-dark-gray">
          By providing your card information, you allow {operatorName || "your operator"} to charge your card for future payments in
          accordance with their terms.
        </Typography>

        <div className="border-t border-neutral-mid-gray" />

        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Card Nickname
          </Typography>
          <TextInput
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Enter Credit Card Nickname"
            disabled={isSubmitting}
            maxLength={60}
          />
          <Typography variant="small" className="text-neutral-silver-gray">
            Optional
          </Typography>
        </div>

        {/* in-use cards can only be replaced, not deleted (matches mobile) */}
        {mode === "edit" && card && !card.metadata.in_use && onRequestDelete && (
          <Button
            type="button"
            variant="custom"
            size="sm"
            startIcon="Trash"
            className="mx-auto !text-danger"
            disabled={isSubmitting}
            onClick={() => onRequestDelete(card)}
          >
            Delete and Remove Credit Card
          </Button>
        )}
      </div>
    </ResponsiveDialog>
  );
};
